import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import CommunityMessagesWidgetComponent from './CommunityMessagesWidgetComponent';

import { confirmSendingDocumentOnly } from '../../Messages/actions';
import {
  dropMessages,
  getChatById,
  getChatInfoById,
  putNewMessage,
  setDocumentAsAttachmentCommunity,
} from '../actions';

import { getLinkToUser } from '../../Messages/utils';

import * as commonSelectors from '../../Shared/selectors';
import * as communitySelectors from '../selectors';

const CommunityMessagesWidgetContainer = ({
  baseUrl,
  currentChatId,
  chats,
  putNewMessage,
  getChatById,
  dropMessages,
  isMobileScreen,
  getThreadsList,
  getChatInfoById,
  attachmentDocumentId,
  attachmentDocumentName,
  setDocumentAsAttachmentCommunity,
  onThreadExistsCheck,
}) => {
  const [isInputDisabled, setIsInputDisabled] = useState(true);
  const [titleWithUrl, setTitleWithUrl] = useState(null);
  const [subtitleWithUrl, setSubtitleWithUrl] = useState(null);
  const [widgetHeader, setWidgetHeader] = useState({});

  const [inputValue, setInputValue] = useState('');
  const [attachModalType, setAttachModalType] = useState(null);
  const [isAttachIconShown, setIsAttachIconShown] = useState(false);

  const turnInputOn = () => {
    setIsInputDisabled(false);
    setIsAttachIconShown(true);
  };

  const turnInputOff = () => {
    setIsInputDisabled(true);
    setIsAttachIconShown(false);
  };

  const clearInputValue = () => {
    setInputValue('');
  };

  const clearCurrentChat = () => {
    setTitleWithUrl(null);
    setSubtitleWithUrl(null);
    setWidgetHeader({});
    turnInputOff();
    clearInputValue();
  };

  const renderChatById = (id) => {
    if (id) {
      dropMessages();
      clearInputValue();
      turnInputOn();
      getChatById(id, true);

      getChatInfoById(id).then((res) => {
        const chatInfoValues = res?.data?.results;
        // eslint-disable-next-line no-use-before-define
        getTitleAndSubtitle(id, chatInfoValues);
      });
    }
  };

  const onRefresh = (updateChats = false) => {
    updateChats && getThreadsList({}, true);
    dropMessages();
    clearInputValue();
    currentChatId ? renderChatById(currentChatId) : clearCurrentChat();
  };

  const getWidgetUrls = (recipient) => {
    setTitleWithUrl(getLinkToUser(recipient));
  };

  const getTitleAndSubtitle = (id, chatsData = []) => {
    let title;
    let recipient;

    const allChats = chatsData || (chats && chats.length ? chats : []);
    const currentChatData = allChats.filter((chat) => chat.id === id)[0];

    if (!isEmpty(currentChatData)) {
      recipient = currentChatData?.recipient;

      title = `${currentChatData.recipient.firstName} ${currentChatData.recipient.lastName}`;
    }

    const widgetHeader = {
      title,
    };

    setWidgetHeader(widgetHeader);

    getWidgetUrls(recipient);
  };

  const handleNewUserMessage = (isDocumentOnly = false) => {
    if (attachmentDocumentId && !inputValue && !isDocumentOnly) {
      confirmSendingDocumentOnly(
        () => handleNewUserMessage(true),
        attachmentDocumentName,
      );
    } else if (attachmentDocumentId || inputValue) {
      const data = {};
      inputValue && (data.text = inputValue);
      attachmentDocumentId && (data.document = attachmentDocumentId);
      putNewMessage(currentChatId, data).then(() => onRefresh(true));
      setDocumentAsAttachmentCommunity(null, null);
    }
  };

  useEffect(() => {
    dropMessages();
  }, []);

  useEffect(() => {
    onRefresh();
  }, [currentChatId]);

  useEffect(() => {
    if (currentChatId && isInputDisabled) turnInputOn();
  });

  return (
    <CommunityMessagesWidgetComponent
      currentChatId={currentChatId}
      handleNewUserMessage={handleNewUserMessage}
      widgetHeader={widgetHeader}
      isInputDisabled={isInputDisabled}
      titleWithUrl={titleWithUrl}
      subtitleWithUrl={subtitleWithUrl}
      chats={chats}
      isMobileScreen={isMobileScreen}
      getChatById={getChatById}
      setInputValue={setInputValue}
      inputValue={inputValue}
      baseUrl={baseUrl}
      attachModalType={attachModalType}
      setAttachModalType={setAttachModalType}
      isAttachIconShown={isAttachIconShown}
      attachmentDocumentId={attachmentDocumentId}
      attachmentDocumentName={attachmentDocumentName}
      setDocumentAsAttachment={setDocumentAsAttachmentCommunity}
      checkNewAttachment={onThreadExistsCheck}
    />
  );
};

CommunityMessagesWidgetContainer.defaultProps = {
  nextMessages: null,
  chats: [],
  currentChatId: null,
  attachmentDocumentId: null,
  attachmentDocumentName: null,
};

CommunityMessagesWidgetContainer.propTypes = {
  attachmentDocumentId: PropTypes.number,
  attachmentDocumentName: PropTypes.string,
  nextMessages: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
  }),
  getThreadsList: PropTypes.func.isRequired,
  getChatInfoById: PropTypes.func.isRequired,
  putNewMessage: PropTypes.func.isRequired,
  getChatById: PropTypes.func.isRequired,
  dropMessages: PropTypes.func.isRequired,
  chats: PropTypes.instanceOf(Array),
  currentChatId: PropTypes.number,
  baseUrl: PropTypes.string.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
  setDocumentAsAttachmentCommunity: PropTypes.func.isRequired,
  onThreadExistsCheck: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const currentChatId = Number(ownProps?.match.params.id ?? null);
  const isMobileScreen = commonSelectors.getIsMobileScreen(state);
  const chats = communitySelectors.getCommunityCurrentChat(state);
  const {
    documentId: attachmentDocumentId,
    documentName: attachmentDocumentName,
  } = communitySelectors.getCommunityCurrentMessage(state);
  return {
    currentChatId,
    chats,
    isMobileScreen,
    attachmentDocumentId,
    attachmentDocumentName,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getChatInfoById: (chatId) => getChatInfoById(dispatch, chatId),
  putNewMessage: (chatId, data) => putNewMessage(dispatch, chatId, data),
  getChatById: (chatId, queryParams, withReset = false) =>
    getChatById(dispatch, chatId, queryParams, withReset),
  dropMessages: () => dropMessages(dispatch),
  setDocumentAsAttachmentCommunity: (documentId, documentName) =>
    setDocumentAsAttachmentCommunity(dispatch, documentId, documentName),
  confirmSendingDocumentOnly: (sendDocumentOnly, attachmentDocumentName) =>
    confirmSendingDocumentOnly(
      dispatch,
      sendDocumentOnly,
      attachmentDocumentName,
    ),
  onThreadExistsCheck: () => true,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CommunityMessagesWidgetContainer);
